import type { ButtonHTMLAttributes } from 'react';
import styled from 'styled-components';

import {
  BORDER_RADIUS,
  BORDER_WIDTH,
  BUTTON_COLORS_BY_VARIANT,
  BUTTON_CONFIG_BY_SIZE,
  type ButtonColors,
  type ButtonConfig,
} from '@wecasa/common/src/components/button/constants';
import type { CommonButtonProps } from '@wecasa/common/src/types/components';
import colors from '@wecasa/common/src/utils/colors';

import { translate } from '@wecasa/i18n/src/translator';

import { useOnClickThrottled } from '@wecasa/react-hooks/src/use-on-click-throttled';

import { TEXT_FONT } from '../../constants/styles';
import Icon from '../icon/icon';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import type { WithClassNameProps } from '../types';
import { BUTTON_TEXT_BY_SIZE, StyledLoadingWrapper } from './common';
import { disabledStyle } from './text-button';

const IconWrapper = styled.span<{
  $iconRightMargin: ButtonConfig['iconRightMargin'];
}>`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-right: ${({ $iconRightMargin }) => $iconRightMargin}px;
  height: auto;
`;

const StyledButton = styled.button<{
  $height: ButtonConfig['height'];
  $paddingHorizontal: ButtonConfig['paddingHorizontal'];
  $borderColor: ButtonColors['borderColor'];
  $backgroundColors: ButtonColors['backgroundColors'];
  $hasFullWidth: boolean;
  $isDisabled: boolean;
}>`
  position: relative;
  display: inline-flex;
  font-family: ${TEXT_FONT};
  box-sizing: border-box;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s ease-out;
  cursor: pointer;
  ${({ $isDisabled, $height, $paddingHorizontal, $borderColor, $backgroundColors, $hasFullWidth }) => `
      ${$isDisabled ? disabledStyle : ''}
      height: ${$height}px;
      padding: 0 ${$paddingHorizontal}px;
      border: ${BORDER_WIDTH}px solid ${$borderColor};
      border-radius: ${BORDER_RADIUS}px;
      background: ${$backgroundColors.default};
      width: ${$hasFullWidth ? '100%' : 'auto'};
      
      &:hover {
        background: ${$isDisabled ? $backgroundColors.default : $backgroundColors.hover};
      }
  
      &:active {
        background: ${$isDisabled ? $backgroundColors.default : $backgroundColors.active};
      }
    `}
`;

const t = translate('components.button');

// We want to make onPress props optional if the type of the button is 'submit'
// But this makes the 'Modal' buttons and test file bug even though the rest is working
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & CommonButtonProps & WithClassNameProps;

const Button = ({
  children,
  hasFullWidth = false,
  iconName,
  isDisabled,
  isLoading,
  onPress,
  size = 'md',
  variant = 'primary',
  className,
  ...props
}: ButtonProps) => {
  const ButtonText = BUTTON_TEXT_BY_SIZE[size];
  const { iconSize, height, paddingHorizontal, paddingHorizontalWithIcon, iconRightMargin } =
    BUTTON_CONFIG_BY_SIZE[size];
  const { iconColor, loadingSpinnerColor, color, borderColor, backgroundColors } = BUTTON_COLORS_BY_VARIANT[variant];
  // To avoid rapid double clicks
  const onPressThrottled = useOnClickThrottled(onPress);
  const shouldBeDisabled = !!(isDisabled || isLoading);

  return (
    <StyledButton
      $height={height}
      $paddingHorizontal={iconName ? paddingHorizontalWithIcon : paddingHorizontal}
      $borderColor={borderColor}
      $backgroundColors={backgroundColors}
      $hasFullWidth={hasFullWidth}
      $isDisabled={shouldBeDisabled}
      className={className}
      disabled={shouldBeDisabled}
      onClick={onPressThrottled}
      type='button'
      {...props}
    >
      {!!iconName && (
        <IconWrapper $iconRightMargin={iconRightMargin}>
          <Icon name={iconName} color={isLoading ? colors.transparent : iconColor} size={iconSize} />
        </IconWrapper>
      )}
      {isLoading && (
        <StyledLoadingWrapper>
          <LoadingSpinner color={loadingSpinnerColor} title={t('.loading')} />
        </StyledLoadingWrapper>
      )}
      <ButtonText color={isLoading ? colors.transparent : color} centered>
        {children}
      </ButtonText>
    </StyledButton>
  );
};

export default Button;
